import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, IconButton, MenuItem, Select, Typography, styled } from '@mui/material';
import zeplinColor from 'theme/Color';

const NavButton = styled(IconButton)(({ theme, disabled }) => ({
  padding: '8px 12px',
  borderRadius: '8px',
  border: `1px solid ${disabled ? zeplinColor.Neutral5 : zeplinColor.BasicBlack}`,
  backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
  '&:hover': {
    backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.BasicBlack,
    opacity: 0.9,
  },
  '&.Mui-disabled': {
    backgroundColor: zeplinColor.Neutral3,
  },
}));

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

export const CustomPagination = ({
  currentPage,
  totalPages,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}: PaginationProps) => {
  const startRange = currentPage * rowsPerPage + 1;
  const endRange = Math.min((currentPage + 1) * rowsPerPage, totalCount);
  const currentPageColor = currentPage === 0 ? zeplinColor.Neutral9 : zeplinColor.Neutral1;
  const navButtonColor =
    currentPage >= totalPages - 1 ? zeplinColor.Neutral9 : zeplinColor.Neutral1;

  const getPageRange = () => {
    const delta = 1;
    const range = [];
    for (let i = 0; i < totalPages; i++) {
      if (
        i === 0 ||
        i === totalPages - 1 ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      } else if (range[range.length - 1] !== '...') {
        range.push('...');
      }
    }
    return range;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: zeplinColor.Neutral1,
        borderRadius: '8px',
        marginTop: '16px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Typography variant='body2' color={zeplinColor.BasicBlack}>
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          IconComponent={KeyboardArrowDownIcon}
          size='small'
          sx={{
            height: '32px',
            borderRadius: '6px',
            '& .MuiSelect-select': { padding: '4px 8px' },
          }}
        >
          {[5, 10, 20, 50].map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
        <Typography variant='body2' color={zeplinColor.Neutral9}>
          {startRange}-{endRange} of {totalCount}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <NavButton onClick={() => onPageChange(0)} disabled={currentPage === 0}>
          <KeyboardDoubleArrowLeftIcon
            sx={{
              fontSize: 18,
              color: currentPageColor,
            }}
          />
        </NavButton>

        <NavButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
          <KeyboardArrowLeftIcon
            sx={{
              fontSize: 18,
              color: currentPageColor,
            }}
          />
        </NavButton>

        <Box sx={{ display: 'flex', gap: '4px', mx: 1 }}>
          {getPageRange().map((page, idx) =>
            page === '...' ? (
              <Typography key={`dots-${idx}`} sx={{ px: 1 }}>
                ...
              </Typography>
            ) : (
              <IconButton
                key={page}
                onClick={() => onPageChange(page as number)}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: `1px solid ${zeplinColor.Neutral5}`,
                  backgroundColor: currentPage === page ? zeplinColor.BasicBlack : 'transparent',
                  color: currentPage === page ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                  '&:hover': {
                    backgroundColor:
                      currentPage === page ? zeplinColor.BasicBlack : zeplinColor.Neutral3,
                  },
                }}
              >
                <Typography variant='caption' fontWeight='bold'>
                  {(page as number) + 1}
                </Typography>
              </IconButton>
            ),
          )}
        </Box>

        <NavButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
        >
          <KeyboardArrowRightIcon
            sx={{
              fontSize: 18,
              color: navButtonColor,
            }}
          />
        </NavButton>

        <NavButton
          onClick={() => onPageChange(totalPages - 1)}
          disabled={currentPage >= totalPages - 1}
        >
          <KeyboardDoubleArrowRightIcon
            sx={{
              fontSize: 18,
              color: navButtonColor,
            }}
          />
        </NavButton>
      </Box>
    </Box>
  );
};
